import { useCallback } from 'react';
import {
  ScheduledSms,
  ScheduledSmsThread,
} from '@weave/schema-gen-ts/dist/schemas/messaging/scheduled/shared/v1/models.pb';
import { OutboundMessageStatus } from '@weave/schema-gen-ts/dist/schemas/messaging/shared/v1/enums.pb';
import { useSchemaQueryUpdaters } from '@frontend/react-query-helpers';
import { PickRequired } from '@frontend/types';
import { serviceName } from '../service';
import { ServiceQueries } from '../types';

const convertScheduledSmsToScheduledSmsThread = ({
  id,
  mediaIds,
  sentAt,
  scheduledAt,
  deletedAt,
  deletedBy,
  updatedAt,
  relatedIds,
  ...rest
}: ScheduledSms): ScheduledSmsThread => ({
  ...rest,
  messageId: id,
  numMedia: mediaIds.length,
  createdAt: new Date().toISOString(),
});

/**
 * This hook is intended to be used by react-query mutation hooks internally in api libraries.
 * Other use cases should use the corresponding mutations instead.
 */
export const useManualSmsScheduledV1QueryUpdaters = () => {
  const queryUpdaters = useSchemaQueryUpdaters<ServiceQueries>(serviceName);

  const createScheduledSms = useCallback(
    ({ scheduledSms }: { scheduledSms: ScheduledSms }) => {
      // Add to `GetThread`
      queryUpdaters.updateQuery({
        endpointName: 'GetThread',
        queryFilters: {
          queryKey: queryUpdaters.getQueryKey<'GetThread'>({
            endpointName: 'GetThread',
            request: {
              threadId: scheduledSms.threadId,
              locationId: scheduledSms.locationId,
            },
          }),
          exact: true,
        },
        updater: (oldData) => ({
          ...oldData,
          scheduledSms: [...oldData.scheduledSmss, scheduledSms],
        }),
      });

      // Add to `ListThreads`
      queryUpdaters.updateQuery({
        endpointName: 'ListThreads',
        queryFilters: {
          predicate: ({ queryKey }) => {
            const request = queryKey[2];
            const requestIncludesLocationId =
              request.locationIds && request.locationIds.length
                ? request.locationIds.includes(scheduledSms.locationId)
                : true;
            const statusIncludesScheduled = request.statuses ? request.statuses.includes(scheduledSms.status) : true;
            return requestIncludesLocationId && statusIncludesScheduled;
          },
        },
        updater: (oldData) => ({
          ...oldData,
          scheduledSmsThreads: [...oldData.scheduledSmsThreads, convertScheduledSmsToScheduledSmsThread(scheduledSms)],
        }),
      });
    },
    [queryUpdaters.getQueryKey, queryUpdaters.updateQuery, convertScheduledSmsToScheduledSmsThread]
  );

  const deleteScheduledSms = useCallback(
    ({ id, locationId, threadId }: Pick<ScheduledSms, 'id' | 'locationId' | 'threadId'>) => {
      // Remove from `GetThread`
      queryUpdaters.updateQuery({
        endpointName: 'GetThread',
        queryFilters: {
          queryKey: queryUpdaters.getQueryKey<'GetThread'>({
            endpointName: 'GetThread',
            request: {
              threadId,
              locationId,
            },
          }),
          exact: true,
        },
        updater: (oldData) => ({
          ...oldData,
          scheduledSms: oldData.scheduledSmss.filter((sms) => sms.id !== id),
        }),
      });

      // Remove from `ListThreads`
      queryUpdaters.updateQuery({
        endpointName: 'ListThreads',
        queryFilters: {
          predicate: ({ queryKey }) => {
            const request = queryKey[2];
            return request.locationIds && request.locationIds.length ? request.locationIds.includes(locationId) : true;
          },
        },
        updater: (oldData) => ({
          ...oldData,
          scheduledSmsThreads: oldData.scheduledSmsThreads.filter((sms) => sms.messageId !== id),
        }),
      });
    },
    [queryUpdaters.updateQuery, queryUpdaters.getQueryKey]
  );

  const updateScheduledSms = useCallback(
    ({
      matchValues,
      newValues,
    }: {
      matchValues: PickRequired<ScheduledSms, 'id' | 'locationId'>;
      newValues: Partial<ScheduledSms>;
    }) => {
      // Update in `GetThread`
      queryUpdaters.updateQuery({
        endpointName: 'GetThread',
        queryFilters: {
          queryKey: queryUpdaters.getQueryKey<'GetThread'>({
            endpointName: 'GetThread',
            request: {
              locationId: matchValues.locationId,
            },
          }),
          predicate: ({ queryKey }) => {
            const request = queryKey[2];
            return matchValues.threadId ? request.threadId === matchValues.threadId : true;
          },
          exact: false,
        },
        updater: (oldData) => ({
          ...oldData,
          scheduledSms: oldData.scheduledSmss.map((sms) =>
            sms.id === matchValues.id ? { ...sms, ...newValues } : sms
          ),
        }),
      });

      // Update in `ListThreads`
      const queriesToUpdate = queryUpdaters.getQueriesData({
        endpointName: 'ListThreads',
        queryFilters: {
          predicate: ({ queryKey }) => {
            const request = queryKey[2];
            return request.locationIds && request.locationIds.length
              ? request.locationIds.includes(matchValues.locationId)
              : true;
          },
        },
      });
      let existingSms: ScheduledSmsThread | undefined;
      queriesToUpdate.forEach(([queryKey, queryData]) => {
        const request = queryKey[2];
        existingSms = queryData.scheduledSmsThreads.find((sms) => sms.messageId === matchValues.id);
        const includesNewLocationId = newValues.locationId
          ? request.locationIds?.length
            ? request.locationIds.includes(newValues.locationId)
            : true
          : true;
        const includesNewStatus = newValues.status
          ? request.statuses?.length
            ? request.statuses.includes(newValues.status)
            : newValues.status !== OutboundMessageStatus.DELETED // Always exclude deleted messages from the query if not explicitly included
          : true;

        if (includesNewStatus && includesNewLocationId && !existingSms) {
          // Invalidate queries where we don't have an existing sms (to get the full object from), but the sms should be added
          queryUpdaters.invalidateQueries({
            endpointName: 'ListThreads',
            queryFilters: {
              queryKey,
              exact: true,
            },
          });
        } else {
          queryUpdaters.updateQuery({
            endpointName: 'ListThreads',
            queryFilters: {
              queryKey,
              exact: true,
            },
            updater: (oldData) => {
              return {
                ...oldData,
                scheduledSmsThreads:
                  // Filter the message out if it no longer matches the query
                  includesNewLocationId && includesNewStatus
                    ? oldData.scheduledSmsThreads.map((sms) => {
                        if (sms.messageId !== matchValues.id) return sms;
                        if (!existingSms) existingSms = sms;
                        return { ...sms, ...newValues };
                      })
                    : oldData.scheduledSmsThreads.filter((sms) => sms.messageId !== matchValues.id),
              };
            },
          });
        }
      });
    },
    []
  );

  return {
    createScheduledSms,
    deleteScheduledSms,
    updateScheduledSms,
    ...queryUpdaters,
  };
};
